import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useData } from "../providers/DataProvider";
import { useSnackbar } from "../providers/SnackbarProvider";
import CustomizerOverlay from "../components/CustomizerOverlay";
import CustomizerCanvas from "../components/CustomizerCanvas";
import CustomizerPanel from "../components/CustomizerPanel";
import CustomizerToolbar from "../components/CustomizerToolbar";
import CustomizerDrawer from "../components/CustomizerDrawer";
import TopBanner from "../components/TopBanner";
import CartButton from "../components/CartButton";
import MobileHeader from "../components/mobile/MobileHeader";
import OutOfPointsModal from "../components/modals/OutOfPointsModal";
import PurchasePatchPointsModal from "../components/modals/PurchasePatchPointsModal";
import ShopifyGetPatchPointsPacks from "../utils/ShopifyGetPatchPointsPacks";
import {
  checkPolyInPoly,
  getBoundingRectFromCoords,
  getCenteredCoordinates,
  polyPolyCollision,
} from "../utils/collision-detection";
import { useLocation, useSearchParams } from "react-router-dom";
import ShopifyGetPatches from "../utils/ShopifyGetPatches";
import SaveCustomizerDesign from "../utils/SaveCustomizerDesign";
import GetCustomizerDesign from "../utils/GetCustomerDesign";
import ShopifyCreateCheckout from "../utils/ShopifyCreateCheckout";
import GetCustomer from "../utils/GetCustomer";
import ShopifyCreateMultipassToken from "../utils/ShopifyCreateMultipassToken";
import ShopifyCreateCustomerAccessTokenWithMultipass from "../utils/ShopifyCreateCustomerAccessTokenWithMultipass";
import getDataVolume from "../utils/getDataVolume";
import CartModal from "../components/modals/CartModal";
import {
  getCustomizationLineItem,
  getLineItemsWithMergedQuantity,
  getMiscLineItems,
  getPatchLineItems,
  getPatchPointsPackLineItems,
} from "../utils/CheckoutHelpers";
import ShopifyGetProductVariant from "../utils/ShopifyGetProductVariant";
import JSONCrush from "jsoncrush";
import WorkZoneWrapper from "../components/mobile/WorkZoneWrapper";
import useMobileMode from "../hooks/useMobileMode";
import useCheckout from "../hooks/useCheckout";
import HeaderKioskComponent from "../components/HeaderKioskComponent";

const style = {
  display: "flex",
  height: "100%",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
};

const StyledCanvasViewport = styled(Box, {
  shouldForwardProp: (props) => props !== "mouse" && props !== "orderPreviewMode",
})(({ mouse, orderPreviewMode, theme }) => ({
  display: "flex",
  flexDirection: "column-reverse",
  padding: orderPreviewMode ? "25px 190px 25px 25px" : "25px 25px 55px 25px",
  overflow: mouse.dragging ? "hidden" : "auto",
  position: "relative",
  zIndex: 1,
  alignItems: "center",
  flexBasis: "100%",
  userSelect: "none",
  [theme.breakpoints.up("laptop")]: {
    marginBottom: orderPreviewMode ? "0" : "148px",
  },
  [theme.breakpoints.down("laptop")]: {
    padding: "25px",
  },
}));

const StyledCanvasWrapper = styled(Box, {
  shouldForwardProp: (props) => props !== "canvasImage",
})(({ canvasImage, theme }) => ({
  margin: "0 auto",
  position: "relative",
  width: canvasImage.width * canvasImage.zoom,
  height: canvasImage.height * canvasImage.zoom,
  overflow: "hidden",
  flexShrink: "0",
  [theme.breakpoints.down("laptop")]: {
    overflow: "initial",
  },
}));

const StyledToolbarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "kiosk",
})(({ theme, kiosk }) => ({
  position: "fixed",
  top: kiosk ? "160px" : "40px",
  right: "25px",
  zIndex: 2,
}));

export default function Customizer() {
  const data = useData();
  const { saveDesignState, snackbarState } = useSnackbar();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useMobileMode();
  const { previousCartLineItems, lastIncompleteCheckout, setPreviousCartLineItems } = useCheckout(
    data.customer
  );
  const viewportRef = React.useRef();
  const canvasViewportRef = React.useRef();
  const canvasWrapperRef = React.useRef();
  const [orderPreviewMode, setOrderPreviewMode] = React.useState(false);
  const [modal, setModal] = React.useState(null);
  const [canvasImage, setCanvasImage] = React.useState(null);
  const [canvasZones, setCanvasZones] = React.useState([]);
  const [canvasPatches, setCanvasPatches] = React.useState(
    () => JSON.parse(sessionStorage.getItem(location.pathname))?.patches || []
  );
  const [canvasHistory, setCanvasHistory] = React.useState([]);
  const [dragData, setDragData] = React.useState(null);
  const [mouse, setMouse] = React.useState({
    x: 0,
    y: 0,
    down: false,
    dragging: false,
  });
  const [cart, setCart] = React.useState(() => ({
    addOnPoints: JSON.parse(sessionStorage.getItem(location.pathname))?.points || [],
    pointsAvailable: 0,
    pointsUsed: 0,
    total: 0,
    loading: false,
  }));
  const [shopifyCartItems, setShopifyCartItems] = React.useState([]);
  const [currentCartLineItems, setCurrentCartLineItems] = React.useState([]);
  const [openMobileView, setMobileView] = React.useState(false);
  const [mobileActivePatch, setMobileActivePatch] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [favoritePatches, setFavoritePatches] = React.useState([]);
  const messagePerSession = React.useRef(true);

  // Effects.

  // Ban pinch zoom effect
  React.useEffect(() => {
    if (!isMobile) {
      function handleTouchMove(e) {
        if (e.scale !== 1) {
          e.preventDefault();
        }
      }
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
      };
    }
  }, [isMobile]);

  // Add body class.
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);

  // Check if customer was logged in on Shopify, and if so, log them in here.
  React.useEffect(() => {
    if (searchParams.get("c")) {
      const customerId = atob(searchParams.get("c"));
      GetCustomer(customerId).then((customer) => {
        ShopifyCreateMultipassToken(customer.email).then((multipassToken) => {
          ShopifyCreateCustomerAccessTokenWithMultipass(multipassToken).then((accessToken) => {
            sessionStorage.setItem("sup_token", accessToken);
            data.setToken(accessToken);
          });
        });
      });
    }
  }, []);

  // Check if the customer had an existing cart on Shopify.
  React.useEffect(() => {
    if (searchParams.get("cartItems")) {
      const items = JSON.parse(searchParams.get("cartItems"));
      ShopifyGetPatchPointsPacks().then((packs) => {
        items.forEach((item) => {
          const id = `gid://shopify/ProductVariant/${item.variant}`;
          const find = packs.find((pack) => pack.id === id);
          if (find) {
            for (let i = 1; i <= item.quantity; i++) {
              handleAddPatchPoints(find);
            }
          } else {
            ShopifyGetProductVariant(item.variant).then((variant) => {
              setShopifyCartItems((items) =>
                items.concat({
                  ...variant,
                  quantity: item.quantity,
                })
              );
            });
          }
        });
      });
    }
  }, []);

  // Check if the Customizer is displaying an order.
  React.useEffect(() => {
    if (searchParams.get("data")) {
      let patches = [];
      let addOnPoints = [];
      setOrderPreviewMode(true);

      try {
        patches = JSON.parse(
          JSONCrush.uncrush(decodeURIComponent(location.search?.split("=")[1]?.split("&")[0]))
        );
        addOnPoints = JSON.parse(JSONCrush.uncrush(decodeURIComponent(searchParams.get("points"))));
      } catch (error) {
        console.error(error);
      }
      if (patches?.length && data.variant) {
        addOnPoints.length && setCart((prevState) => ({ ...prevState, addOnPoints }));

        ShopifyGetPatches(patches.map((e) => e.id)).then((results) => {
          setCanvasPatches(
            patches.reduce((array, patch) => {
              const find = results.find((result) => result.id === patch.id);
              if (find) {
                const width = find.width * data.variant.ppi;
                const height = find.height * data.variant.ppi;
                array.push({
                  ...find,
                  center: patch.cen,
                  coordinates: patch.coo,
                  position: patch.pos,
                  rotation: patch.rot,
                  top: patch.cen[1] - height / 2,
                  left: patch.cen[0] - width / 2,
                  visible: true,
                });
              }
              return array;
            }, [])
          );
        });
      }
    }
  }, [data.variant]);

  // Check if the Customizer is loading a saved design.
  React.useEffect(() => {
    if (searchParams.get("design")) {
      const split = searchParams.get("design").split("-");
      const customer = split[0];
      const design = split[1];
      GetCustomizerDesign(customer, design).then((design) => {
        if (design) {
          const patches = design.pat;
          const cart = design?.cart;
          cart && setCart(cart);
          if (patches.length && data.variant) {
            ShopifyGetPatches(patches.map((e) => e.id)).then((results) => {
              setCanvasPatches(
                patches.reduce((array, patch) => {
                  const find = results.find((result) => result.id === patch.id);
                  if (find) {
                    const width = find.width * data.variant.ppi;
                    const height = find.height * data.variant.ppi;
                    array.push({
                      ...find,
                      center: patch.cen,
                      coordinates: patch.coo,
                      bounds: patch.bou,
                      position: patch.pos,
                      rotation: patch.rot,
                      top: patch.cen[1] - height / 2,
                      left: patch.cen[0] - width / 2,
                      visible: true,
                    });
                  }
                  return array;
                }, [])
              );
            });
          }
        } else {
          // did not find design
        }
      });
    }
  }, [data.variant]);

  // Set canvas image.
  React.useEffect(() => {
    if (data.variant) {
      handleToolbarChange("front", true);
      // need better solution for resetting canvas on variant change
      // setCanvasPatches([]);
      // setCanvasHistory([]);
    }
  }, [data.variant]);

  // Set zones.
  React.useEffect(() => {
    if (data.variant && canvasImage) {
      setCanvasZones(
        data.variant.zones.filter((e) => e.zone_metadata.position === canvasImage.view)
      );
    }
  }, [data.variant, canvasImage]);

  // Set canvas history.
  React.useEffect(() => {
    const prevHistory = canvasHistory[canvasHistory.length - 1] || {
      image: {},
      patches: [],
    };
    const imageChange = prevHistory.image?.view !== canvasImage?.view;
    const patchLengthChange = prevHistory.patches.length !== canvasPatches.length;
    const patchCoordsChange =
      JSON.stringify(prevHistory.patches.map((e) => e.coordinates)) !==
      JSON.stringify(canvasPatches.map((e) => e.coordinates));
    if (imageChange || patchLengthChange || patchCoordsChange) {
      setCanvasHistory((state) =>
        state.concat({
          image: canvasImage,
          patches: canvasPatches,
        })
      );
    }
  }, [canvasImage, canvasPatches]);

  // Manage line items for current customization.
  React.useEffect(() => {
    if (data.variant) {
      const customizationLineItem = getCustomizationLineItem(
        data.product,
        data.variant,
        canvasPatches,
        cart?.addOnPoints,
        data?.characterName
      );
      const patchLineItems = getPatchLineItems(canvasPatches);
      const patchPointsPacksLineItems = getPatchPointsPackLineItems(cart.addOnPoints);
      const miscLineItems = getMiscLineItems(shopifyCartItems);
      setCurrentCartLineItems([
        customizationLineItem,
        ...patchLineItems,
        ...patchPointsPacksLineItems,
        ...miscLineItems,
      ]);
    }
  }, [data.product, data.variant, canvasPatches, cart.addOnPoints, shopifyCartItems]);

  // Manage points available.
  React.useEffect(() => {
    const pointsIncluded =
      data.specialAffiliate?.has_double_patch_points && data.product
        ? data.product.pointsIncluded * 2
        : data?.product?.pointsIncluded || 0; // double patch points for kiosk mode
    const customerPointsBalance = data.customer ? data.customer.points : 0;
    setCart((state) => ({
      ...state,
      pointsAvailable:
        pointsIncluded +
        customerPointsBalance +
        state.addOnPoints.reduce((sum, item) => sum + item.points, 0),
    }));
  }, [data.customer, data.product, cart.addOnPoints]);

  // Manage points used.
  React.useEffect(() => {
    setCart((state) => ({
      ...state,
      pointsUsed: canvasPatches.reduce((sum, patch) => sum + patch.points, 0),
    }));
  }, [canvasPatches.length]);

  // Manage cart total.
  React.useEffect(() => {
    if (data.variant) {
      const currentCartTotal = currentCartLineItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const previousCartTotal = previousCartLineItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      setCart((state) => ({
        ...state,
        total: currentCartTotal + previousCartTotal,
      }));
    }
  }, [currentCartLineItems, previousCartLineItems]);

  // Manage window resizing.
  React.useEffect(() => {
    const resize = () => {
      if (canvasImage) {
        setCanvasImage((state) => ({
          ...state,
          zoom: getMinZoom(canvasImage),
        }));
      }
    };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [canvasImage]);

  // Handlers.

  const handleToolbarChange = React.useCallback(
    (value, render) => {
      switch (value) {
        case "zoomIn":
          setCanvasImage((state) => ({
            ...state,
            zoom: state.zoom + 0.25 <= 1 ? state.zoom + 0.25 : 1,
          }));
          break;
        case "zoomOut":
          const min = getMinZoom(canvasImage);
          setCanvasImage((state) => ({
            ...state,
            zoom: state.zoom - 0.25 >= min ? state.zoom - 0.25 : min,
          }));
          break;
        case "undo":
          const prevHistory = canvasHistory[canvasHistory.length - 2];
          if (prevHistory) {
            setCanvasHistory((state) => state.slice(0, state.length - 1));
            setCanvasPatches(prevHistory.patches);
            setCanvasImage(prevHistory.image);
          }
          break;
        case "front":
        case "back":
        case "left":
        case "right":
          const image = data.variant.images.find((e) => e.view === value);

          !render && data.audios.VIEW.play();

          setCanvasImage({
            ...image,
            zoom: getMinZoom(image),
          });
          break;
      }
    },
    [data.variant, canvasImage, canvasHistory, data.audios]
  );

  const handleAddPatchPoints = React.useCallback((option) => {
    setCart((state) => ({
      ...state,
      addOnPoints: [...state.addOnPoints, option],
    }));
    setModal(null);
  }, []);

  const handleRemovePatchPoints = React.useCallback(
    (id) => {
      const findIndex = cart.addOnPoints.findIndex((e) => e.id === id);
      setCart((state) => ({
        ...state,
        addOnPoints: state.addOnPoints.filter((e, i) => i !== findIndex),
      }));
    },
    [cart.addOnPoints]
  );

  const handleRemoveShopifyCartItem = React.useCallback(
    (id) => {
      const findIndex = shopifyCartItems.findIndex((e) => e.id === id);
      setShopifyCartItems((items) => items.filter((e, i) => i !== findIndex));
    },
    [shopifyCartItems]
  );

  const handleOpenCart = React.useCallback(() => {
    setModal({ type: "CART" });
  }, []);

  const handleCreateCheckout = React.useCallback(
    (keepShopping) => {
      const lineItems = getLineItemsWithMergedQuantity([
        ...currentCartLineItems,
        ...previousCartLineItems,
      ]);
      const orderAttributes = data.customer
        ? [
            {
              key: "_customerId",
              value: data.customer.id.replace("gid://shopify/Customer/", ""),
            },
            {
              key: "_isKiosk",
              value: data.isKiosk.toString(),
            },
          ]
        : [];

      data.setLoading(true);
      ShopifyCreateCheckout(data.token, lineItems, orderAttributes, data.isKiosk).then(
        (checkout) => {
          if (checkout) {
            if (keepShopping) {
              if (checkout.multipassToken) {
                window.location.assign(
                  `https://${process.env.REACT_APP_SHOP_URL}/account/login/multipass/${checkout.multipassToken}`
                );
              } else {
                window.location.assign(
                  data.product.onlineStoreUrl
                    ? data.product.onlineStoreUrl
                    : `https://${process.env.REACT_APP_SHOP_URL}`
                );
              }
            } else {
              window.location.assign(checkout.checkoutUrl);
            }
          }
        }
      );
    },
    [data.token, data.customer, currentCartLineItems, previousCartLineItems, data.isKiosk]
  );

  const handleSaveDesign = React.useCallback(
    (name, filteredDesignOk) => {
      const split = searchParams.get("design")?.split("-");
      const designId = split && split[1];
      const characterName = data?.characterName;
      const designs = { ...data.customer.savedDesigns };
      const designName = Date.now();
      designs[designId || designName] = {
        name,
        pro: data.product.id.replace("gid://shopify/Product/", ""),
        var: data.variant.id.replace("gid://shopify/ProductVariant/", ""),
        characterName,
        pat: canvasPatches.map((e) => ({
          id: e.id,
          cen: e.center,
          coo: e.coordinates,
          pos: e.position,
          rot: e.rotation,
          bou: e.bounds,
          top: e.top,
          lef: e.left,
        })),
        cart,
      };

      const blob = getDataVolume(designs);

      if (blob.size > 50000 && !filteredDesignOk) {
        const { size } = getDataVolume(designs[designId || designName]);

        return {
          message:
            "You have reached the limit of your saved design space. If you would like to save this design, please delete a design to proceed.",
          data: {
            designs,
            designId,
            size,
            flag: false,
            sum: 0,
          },
        };
      } else {
        SaveCustomizerDesign(data.customer.id, filteredDesignOk ? filteredDesignOk : designs).then(
          (result) => {
            if (result) {
              data.setCustomer((prevState) => {
                const newState = {
                  ...prevState,
                  savedDesigns: filteredDesignOk ? filteredDesignOk : designs,
                };
                localStorage.setItem(newState.id, JSON.stringify(newState.savedDesigns));
                return newState;
              });
              saveDesignState("success");
            } else {
              saveDesignState("error");
            }
          }
        );
      }
    },
    [data.customer, data.product, data.variant, canvasPatches]
  );

  // Mouse/touch DOWN.

  const handleMouseDown = (event) => {
    universalMouseDown(event);
  };

  const handleTouchStart = (event) => {
    universalMouseDown(event);
  };

  // Mouse/touch UP.

  const handleMouseUp = (event) => {
    universalMouseUp(event);
  };

  const handleTouchEnd = (event) => {
    universalMouseUp(event);
  };

  const handleTouchCancel = (event) => {
    universalMouseUp(event);
  };

  // Mouse/touch MOVE.

  const handleMouseMove = (event) => {
    if (event.target.matches(".draggable") || dragData) {
      if (event.buttons === 1) {
        const { clientX, clientY } = event;
        let { patch } = event.target.dataset;
        if (patch) patch = JSON.parse(patch);
        universalMouseMove(clientX, clientY, patch);
      }
    }
  };

  const handleTouchMove = (event) => {
    if (event.target.matches(".draggable") || dragData) {
      if (event.touches.length === 1) {
        const { clientX, clientY } = event.touches[0];
        let { patch } = event.target.dataset;
        if (patch) patch = JSON.parse(patch);
        universalMouseMove(clientX, clientY, patch);
      }
    }
  };

  // Universal mouse handlers.

  const universalMouseDown = (event) => {
    setMouse((state) => ({
      ...state,
      down: true,
    }));
    if (event.target.matches(".draggable")) {
      setMouse((state) => ({
        ...state,
        dragging: true,
      }));
    }
  };

  const universalMouseUp = (event) => {
    setMouse((state) => ({
      ...state,
      down: false,
      dragging: false,
    }));
    // If drag data, event was drag.
    if (dragData) {
      // If canvas is droppable.
      if (canvasZones.some((zone) => zone.droppable && !zone.collision)) {
        dragData?.centerback
          ? data.audios.SUPER_SIZE_FIT_PATCH.play()
          : data.audios.NORMAL_FIT_PATCH.play();

        const wrapperRect = canvasWrapperRef.current.getBoundingClientRect();
        const top =
          (mouse.y -
            wrapperRect.top -
            (dragData.height * data.variant.ppi * canvasImage.zoom) / 2) /
          canvasImage.zoom;
        const left =
          (mouse.x -
            wrapperRect.left -
            (dragData.width * data.variant.ppi * canvasImage.zoom) / 2) /
          canvasImage.zoom;
        // If patch was already on canvas.
        if (dragData.index >= 0) {
          setCanvasPatches((state) =>
            state.map((e, i) => {
              if (i === dragData.index) {
                return {
                  ...dragData,
                  top,
                  left,
                  visible: true,
                  selected: true,
                };
              } else {
                return e;
              }
            })
          );
          // Patch is new.
        } else {
          // Check if dropping patch would exceed points available.
          if (cart.pointsUsed + dragData.points > cart.pointsAvailable) {
            setModal({ type: "OUT_OF_POINTS" });
          } else {
            setCanvasPatches((state) =>
              state.concat({
                ...dragData,
                position: canvasImage.view,
                top,
                left,
                visible: true,
                selected: true,
              })
            );
            if (canvasPatches.length + 1 === 2 && messagePerSession.current) {
              snackbarState(
                "error",
                "No need to be perfect. Our specialists will align and center your patches. Happy Supermixing!"
              );
              messagePerSession.current = false;
            }
          }
        }
        // Canvas is not droppable.
      } else {
        data.audios.ERROR_FIT_SOUND.play();
        snackbarState("error", "Oops! The patch could not be dropped here.");
        // If patch was already on canvas, make is visible again (coords have not been updated)/
        if (dragData.index >= 0) {
          setCanvasPatches((state) =>
            state.map((e, i) => {
              if (i === dragData.index) {
                return {
                  ...e,
                  visible: true,
                };
              } else {
                return e;
              }
            })
          );
        }
      }
      resetDragRelatedState();
    }
    // If no drag data, event was click.
    if (!dragData) {
      if (event.target.matches(".selectable")) {
        setCanvasPatches((state) =>
          state.map((e, i) => ({
            ...e,
            selected: i === JSON.parse(event.target.dataset.patch).index,
          }))
        );
      } else {
        setCanvasPatches((state) =>
          state.map((e) => ({
            ...e,
            selected: false,
          }))
        );
      }
    }
  };

  const universalMouseMove = (x, y, patch) => {
    setMouse((state) => ({
      ...state,
      x,
      y,
    }));
    // Init drag data.
    if (!dragData) {
      setDragData({
        ...patch,
        rotation: 0,
      });
      // If patch was already on canvas.
      if (patch.index >= 0) {
        setDragData((state) => ({
          ...state,
          index: patch.index,
          rotation: patch.rotation,
        }));
        setCanvasPatches((state) =>
          state.map((e, i) => {
            if (i === patch.index) {
              return {
                ...e,
                visible: false,
              };
            } else {
              return e;
            }
          })
        );
      }
    }
    // If some patch is selected, unselect it (because now dragging).
    if (canvasPatches.some((e) => e.selected)) {
      setCanvasPatches((state) =>
        state.map((e) => ({
          ...e,
          selected: false,
        }))
      );
    }
    // Update drag data position and drag-related state.
    if (dragData) {
      const wrapperRect = canvasWrapperRef.current.getBoundingClientRect();
      const canvasMouseX = (x - wrapperRect.left) / canvasImage.zoom;
      const canvasMouseY = (y - wrapperRect.top) / canvasImage.zoom;
      const canvasDragDataCoordinates = getCenteredCoordinates(
        canvasMouseX,
        canvasMouseY,
        dragData.width * data.variant.ppi,
        dragData.height * data.variant.ppi,
        dragData.rotation
      );
      setDragData((state) => ({
        ...state,
        center: [canvasMouseX, canvasMouseY],
        coordinates: canvasDragDataCoordinates,
        bounds: getBoundingRectFromCoords(canvasDragDataCoordinates),
      }));
      updateCanvasZonesDropState(canvasDragDataCoordinates, dragData.index);
    }
  };

  const handleRotatePatch = React.useCallback(
    (event, patch, index) => {
      const rotation = patch.rotation + 45 < 360 ? patch.rotation + 45 : 0;
      const newCoordinates = getCenteredCoordinates(
        patch.center[0],
        patch.center[1],
        patch.width * data.variant.ppi,
        patch.height * data.variant.ppi,
        rotation
      );
      setCanvasPatches((state) =>
        state.map((e, i) => {
          if (i === index) {
            return {
              ...e,
              rotation: rotation,
              coordinates: newCoordinates,
              bounds: getBoundingRectFromCoords(newCoordinates),
            };
          } else {
            return e;
          }
        })
      );
      updateCanvasZonesDropState(newCoordinates, index);
    },
    [data.variant]
  );

  const handleDeletePatch = React.useCallback((event, index) => {
    setCanvasPatches((state) => state.filter((e, i) => i !== index));
  }, []);

  const handleCloseModal = (event) => {
    setModal(null);
  };

  // Helpers.

  const getMinZoom = (image) => {
    if (viewportRef.current) {
      const viewportRect = viewportRef.current.getBoundingClientRect();
      const canvasPadding = 50;
      if (orderPreviewMode) {
        return Math.min(
          (viewportRect.width - canvasPadding - 190) / image?.width, // 190 = toolbar width
          (viewportRect.height - canvasPadding) / image?.height
        );
      } else {
        return Math.min(
          (viewportRect.width - canvasPadding) / image?.width,
          (viewportRect.height - canvasPadding - 250) / image?.height // 250 = drawer height (max)
        );
      }
    }
  };

  const updateCanvasZonesDropState = (coordinates) => {
    setCanvasZones((state) =>
      state.map((zone) => {
        const isInZone = checkPolyInPoly(zone.zone_geometry, coordinates);
        const isPartiallyInZone = !isInZone && polyPolyCollision(zone.zone_geometry, coordinates);
        return {
          ...zone,
          droppable: isInZone,
          collision: isPartiallyInZone,
        };
      })
    );
  };

  const resetDragRelatedState = () => {
    setMouse((state) => ({
      ...state,
      x: 0,
      y: 0,
    }));
    setCanvasZones((state) =>
      state.map((zone) => ({
        ...zone,
        droppable: false,
        collision: false,
      }))
    );
    setDragData(null);
  };

  const handleFavoritePatch = React.useCallback((updateFunction) => {
    setFavoritePatches(updateFunction);
  }, []);

  const handleSetMobileActivePatch = React.useCallback((activePatch) => {
    setMobileActivePatch(activePatch);
  }, []);

  // Render.

  if (data.loading) {
    return (
      <CustomizerOverlay>
        <CircularProgress />
      </CustomizerOverlay>
    );
  }

  if (!data.loading && !data.variant) {
    return (
      <CustomizerOverlay>
        <Typography>Sorry, that item was not found.</Typography>
      </CustomizerOverlay>
    );
  }

  return (
    <div
      ref={viewportRef}
      onMouseDown={!orderPreviewMode ? handleMouseDown : null}
      onMouseMove={!orderPreviewMode ? handleMouseMove : null}
      onMouseUp={!orderPreviewMode ? handleMouseUp : null}
      onTouchStart={!orderPreviewMode ? handleTouchStart : null}
      onTouchMove={!orderPreviewMode ? handleTouchMove : null}
      onTouchEnd={!orderPreviewMode ? handleTouchEnd : null}
      onTouchCancel={!orderPreviewMode ? handleTouchCancel : null}
      style={style}
    >
      {data.bannerData && <TopBanner bannerData={data.bannerData} />}
      <HeaderKioskComponent data={data} isMobile={isMobile} orderPreviewMode={orderPreviewMode} />
      {isMobile && (
        <MobileHeader title={data.product.title} data={data} drawerOpen={drawerOpen}>
          <CartButton onClick={handleOpenCart} text={`$${cart.total.toFixed(2)}`} price={true} />
        </MobileHeader>
      )}
      {canvasImage && (
        <StyledCanvasViewport
          ref={canvasViewportRef}
          mouse={mouse}
          orderPreviewMode={orderPreviewMode}
        >
          <WorkZoneWrapper
            isMobile={isMobile}
            canvasImage={canvasImage}
            variants={data.variant.images}
            handleToolbarChange={handleToolbarChange}
            openMobileView={openMobileView}
            setMobileView={setMobileView}
            patch={mobileActivePatch}
            setPatch={setMobileActivePatch}
            setDrawerOpen={setDrawerOpen}
            orderPreviewMode={orderPreviewMode}
          >
            <StyledCanvasWrapper ref={canvasWrapperRef} canvasImage={canvasImage}>
              <CustomizerCanvas
                canvasImage={canvasImage}
                canvasZones={canvasZones}
                canvasPatches={canvasPatches}
                handleRotatePatch={handleRotatePatch}
                handleDeletePatch={handleDeletePatch}
              />
            </StyledCanvasWrapper>
          </WorkZoneWrapper>
        </StyledCanvasViewport>
      )}
      {!orderPreviewMode && (
        <CustomizerPanel
          customer={data.customer}
          isMobile={isMobile}
          cart={cart}
          handleAddPatchPoints={handleAddPatchPoints}
          handleCreateCheckout={handleCreateCheckout}
          handleSaveDesign={handleSaveDesign}
          handleOpenCart={handleOpenCart}
          favoritePatches={favoritePatches}
          setFavoritePatches={handleFavoritePatch}
          setMobilePatch={handleSetMobileActivePatch}
        />
      )}

      {!isMobile && (
        <StyledToolbarWrapper kiosk={data.isKiosk}>
          <CartButton
            style={{
              marginBottom: "10px",
            }}
            onClick={handleOpenCart}
            text={"View Cart"}
          />
          <CustomizerToolbar
            canvasImage={canvasImage}
            handleToolbarChange={handleToolbarChange}
            orderPreviewMode={orderPreviewMode}
          />
        </StyledToolbarWrapper>
      )}

      {!orderPreviewMode && (
        <CustomizerDrawer
          setMobilePatch={setMobileActivePatch}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          favoritePatches={favoritePatches}
          setFavoritePatches={setFavoritePatches}
        />
      )}

      {dragData && (
        <img
          src={dragData.src}
          style={{
            position: "absolute",
            top: mouse.y - (dragData.height * data.variant.ppi * canvasImage.zoom) / 2,
            left: mouse.x - (dragData.width * data.variant.ppi * canvasImage.zoom) / 2,
            width: dragData.width * data.variant.ppi * canvasImage.zoom,
            height: dragData.height * data.variant.ppi * canvasImage.zoom,
            transform: `rotate(${dragData.rotation}deg)`,
            zIndex: 5,
          }}
          draggable={false}
        />
      )}

      {modal && modal.type === "OUT_OF_POINTS" && (
        <OutOfPointsModal onClose={handleCloseModal} setModal={setModal} isMobile={isMobile} />
      )}
      {modal && modal.type === "PURCHASE_POINTS" && (
        <PurchasePatchPointsModal
          onClose={handleCloseModal}
          handleAddPatchPoints={handleAddPatchPoints}
          isMobile={isMobile}
        />
      )}
      {modal && modal.type === "CART" && (
        <CartModal
          onClose={handleCloseModal}
          currentCart={currentCartLineItems}
          previousCart={previousCartLineItems}
          setPreviousCartLineItems={setPreviousCartLineItems}
          handleRemovePatchPoints={handleRemovePatchPoints}
          handleRemoveShopifyCartItem={handleRemoveShopifyCartItem}
          lastIncompleteCheckout={lastIncompleteCheckout}
          isMobile={isMobile}
          handleCreateCheckout={handleCreateCheckout}
        />
      )}
    </div>
  );
}
